import { clampSelfieDay, effectiveSelfieDayOfMonth } from './guidedFlowUtils';

/** Data local em ISO (YYYY-MM-DD). Fonte canónica — não duplicar com toISOString().slice. */
export function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Data local em ISO (YYYY-MM-DD). Fonte canónica — não duplicar com toISOString().slice. */
export function toLocalISODate(referenceDate = new Date()) {
  const date = new Date(referenceDate);
  date.setHours(12, 0, 0, 0);
  return toISODate(date);
}

/** Hoje no calendário local (YYYY-MM-DD). */
export function todayISO(referenceDate = new Date()) {
  return toLocalISODate(referenceDate);
}

/**
 * Selfie pode ser criada a qualquer momento (sem bloqueio por fim de ciclo).
 * @deprecated Mantido por compatibilidade — devolve sempre true.
 */
export function isSelfieCreationAvailable(_cycle, _referenceDate = new Date()) {
  return true;
}

export function formatCycleEndDateLabel(endISO, locale = 'pt-PT') {
  if (!endISO) return '';
  const date = new Date(`${endISO}T12:00:00`);
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date);
}

/**
 * Dias inteiros até `endISO` (>=0), a partir de `referenceDate`.
 * .pen: aLDdy (ds/banner/selfie-invite--upcoming) › "Ainda faltam N dias".
 */
export function daysUntil(endISO, referenceDate = new Date()) {
  if (!endISO) return null;
  const end = new Date(`${endISO}T12:00:00`);
  const today = new Date(`${toLocalISODate(referenceDate)}T12:00:00`);
  const diffMs = end.getTime() - today.getTime();
  return Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
}

/**
 * Data sugerida ao fechar um ciclo (dia configurado no perfil; utilizador pode alterar).
 */
export function suggestCycleCloseDate(selfieDayOfMonth, referenceDate = new Date()) {
  const ref = new Date(referenceDate);
  ref.setHours(12, 0, 0, 0);
  const configured = clampSelfieDay(selfieDayOfMonth ?? 28);
  const effective = effectiveSelfieDayOfMonth(configured, ref);

  if (ref.getDate() >= effective) {
    return toISODate(new Date(ref.getFullYear(), ref.getMonth(), effective));
  }
  const prevMonth = ref.getMonth() === 0 ? 11 : ref.getMonth() - 1;
  const prevYear = ref.getMonth() === 0 ? ref.getFullYear() - 1 : ref.getFullYear();
  const prevEffective = effectiveSelfieDayOfMonth(
    configured,
    new Date(prevYear, prevMonth, 1),
  );
  return toISODate(new Date(prevYear, prevMonth, prevEffective));
}

export function formatSelfiePeriodLabel(startISO, endISO, locale = 'pt-PT') {
  const start = new Date(`${startISO}T12:00:00`);
  const end = new Date(`${endISO}T12:00:00`);
  const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

/**
 * Ciclo actual a partir da lista de ciclos (API ou derivada das selfies).
 */
export function getCurrentCycleFromList(cycles = []) {
  return cycles.find((c) => c.isCurrent) || cycles[0] || null;
}

/**
 * Ciclo mensal projectado a partir do dia de fecho (sem clampar o fim a «hoje»).
 * Usado nas Definições para mostrar impacto ao mudar o dia da selfie.
 */
export function projectMonthlyCyclePeriod(selfieDayOfMonth, referenceDate = new Date()) {
  const configured = clampSelfieDay(selfieDayOfMonth ?? 28);
  const ref = new Date(referenceDate);
  ref.setHours(12, 0, 0, 0);
  const anchorThisMonth = new Date(
    ref.getFullYear(),
    ref.getMonth(),
    effectiveSelfieDayOfMonth(configured, ref),
  );

  let start;
  let end;
  if (ref < anchorThisMonth) {
    const prevMonth = ref.getMonth() === 0 ? 11 : ref.getMonth() - 1;
    const prevYear = ref.getMonth() === 0 ? ref.getFullYear() - 1 : ref.getFullYear();
    start = new Date(
      prevYear,
      prevMonth,
      effectiveSelfieDayOfMonth(configured, new Date(prevYear, prevMonth, 1)),
    );
    end = new Date(anchorThisMonth);
    end.setDate(end.getDate() - 1);
  } else {
    start = anchorThisMonth;
    const nextMonth = ref.getMonth() === 11 ? 0 : ref.getMonth() + 1;
    const nextYear = ref.getMonth() === 11 ? ref.getFullYear() + 1 : ref.getFullYear();
    end = new Date(
      nextYear,
      nextMonth,
      effectiveSelfieDayOfMonth(configured, new Date(nextYear, nextMonth, 1)),
    );
    end.setDate(end.getDate() - 1);
  }

  return {
    start: toISODate(start),
    end: toISODate(end),
    startDate: start,
    endDate: end,
  };
}

/**
 * @deprecated Prefer ``listSelfieCyclesFromSnapshots`` / API ``/snapshots/cycles/``.
 * Mantido para testes legados — não reflecte ciclos variáveis.
 */
export function resolveSelfiePeriod(selfieDayOfMonth, referenceDate = new Date()) {
  const period = projectMonthlyCyclePeriod(selfieDayOfMonth, referenceDate);
  const ref = new Date(referenceDate);
  ref.setHours(12, 0, 0, 0);
  const end = new Date(period.endDate);
  if (ref < end) {
    period.endDate = new Date(ref);
    period.end = toISODate(period.endDate);
  }
  return period;
}
